import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, GripVertical, AlertCircle } from "lucide-react";
import {
  adminListQuickPicks,
  adminListTools,
  adminReplaceQuickPicks,
} from "@/admin/cmsApi";
import { BilingualField, FieldError, Label, SelectInput, TextInput } from "@/admin/cmsForms";

const BLANK = () => ({ id: "", need: { ar: "", en: "" }, tool_slug: "" });

export default function AdminQuickPicks() {
  const [picks, setPicks] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const load = useCallback(async () => {
    setErrorMsg("");
    setLoading(true);
    try {
      const [qp, tls] = await Promise.all([adminListQuickPicks(), adminListTools()]);
      setPicks((qp.items || []).map((p) => ({
        id: p.id,
        need: p.need || { ar: "", en: "" },
        tool_slug: p.tool_slug || "",
      })));
      setTools(tls.items || []);
    } catch (e) {
      setErrorMsg(e.message || "Failed to load quick picks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toolOptions = useMemo(
    () => tools.map((t) => ({ value: t.slug, label: `${t.name} (${t.slug})` })),
    [tools]
  );

  const updatePick = (idx, patch) => {
    setPicks((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
    setFieldErrors({});
  };
  const updateNeed = (idx, side, v) => {
    setPicks((prev) => prev.map((p, i) => (i === idx ? { ...p, need: { ...p.need, [side]: v } } : p)));
    setFieldErrors({});
  };
  const addPick = () => {
    if (picks.length >= 4) return;
    setPicks((p) => [...p, BLANK()]);
  };
  const removePick = (idx) => {
    setPicks((p) => p.filter((_, i) => i !== idx));
    setFieldErrors({});
  };
  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= picks.length) return;
    setPicks((p) => {
      const out = [...p];
      const [row] = out.splice(idx, 1);
      out.splice(target, 0, row);
      return out;
    });
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSavedAt(null);
    setErrorMsg("");
    setFieldErrors({});

    // Client-side validation
    const nextErrors = {};
    picks.forEach((p, i) => {
      if (!p.id.trim()) nextErrors[`picks.${i}.id`] = "Required";
      if (!p.tool_slug.trim()) nextErrors[`picks.${i}.tool_slug`] = "Pick a tool";
      if (!p.need.ar.trim() && !p.need.en.trim()) {
        nextErrors[`picks.${i}.need.ar`] = "Provide at least one label";
      }
    });
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      await adminReplaceQuickPicks(
        picks.map((p) => ({
          id: p.id.trim().toLowerCase(),
          need: p.need,
          tool_slug: p.tool_slug,
        }))
      );
      setSavedAt(new Date());
    } catch (err) {
      if (err.status === 422 && err.fieldErrors) {
        setErrorMsg(Object.values(err.fieldErrors)[0] || "Invalid quick-picks list.");
      } else {
        setErrorMsg(err.message || "Save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-quick-picks-loading">
        <div className="h-6 w-32 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
        <div className="h-10 w-1/2 bg-[color:var(--sas-accent-soft)] rounded animate-pulse mt-3" />
        <div className="mt-6 h-48 rounded-xl bg-white border border-[color:var(--sas-line)] animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="admin-quick-picks">
      <Link
        to="/admin/tools"
        className="inline-flex items-center gap-1.5 text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] mb-6"
      >
        <ArrowLeft size={14} /> Tools
      </Link>
      <div className="sas-eyebrow mb-2">Tools</div>
      <h1 className="sas-h1">Start-here quick picks</h1>
      <p className="sas-body mt-3 max-w-2xl text-[15px]">
        The “Start here” panel at the top of <code className="mx-1 px-1 py-0.5 rounded bg-[color:var(--sas-accent-soft)] text-[12px]">/tools</code>
        shows these picks. Keep it short — the public page surfaces the first 2.
      </p>

      {errorMsg && (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg border border-[color:#fecaca] bg-[#fef2f2] px-3 py-2.5 text-[13px] text-[#991b1b]"
          role="alert"
          data-testid="admin-quick-picks-error"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden />
          <span>{errorMsg}</span>
        </div>
      )}
      {savedAt && !errorMsg && (
        <div
          className="mt-4 rounded-lg border border-[color:#bbf7d0] bg-[#f0fdf4] px-3 py-2.5 text-[13px] text-[#166534]"
          role="status"
          data-testid="admin-quick-picks-saved"
        >
          Saved. The public page reflects the new picks immediately on refresh.
        </div>
      )}

      <form onSubmit={onSave} className="mt-6 flex flex-col gap-5" noValidate>
        {picks.length === 0 ? (
          <div className="border border-dashed border-[color:var(--sas-line)] rounded-xl py-10 text-center">
            <p className="sas-body text-[14px]">No quick picks yet. Add one to get started.</p>
          </div>
        ) : picks.map((p, i) => (
          <div
            key={i}
            className="rounded-xl border border-[color:var(--sas-line)] bg-white p-4 md:p-5 flex flex-col gap-4"
            data-testid={`admin-quick-pick-row-${i}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[color:var(--sas-ink-2)]">
                <GripVertical size={13} aria-hidden /> Pick #{i + 1}
              </span>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs px-2 py-1 rounded border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] disabled:opacity-40" data-testid={`admin-quick-pick-up-${i}`}>Up</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === picks.length - 1} className="text-xs px-2 py-1 rounded border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] disabled:opacity-40" data-testid={`admin-quick-pick-down-${i}`}>Down</button>
                <button type="button" onClick={() => removePick(i)} className="text-xs px-2 py-1 rounded border border-[color:var(--sas-line)] hover:border-[#b91c1c] hover:text-[#b91c1c]" data-testid={`admin-quick-pick-remove-${i}`}>
                  <Trash2 size={12} aria-hidden />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
              <div className="flex flex-col gap-1.5">
                <Label required hint="URL-safe">Pick id</Label>
                <TextInput value={p.id} onChange={(v) => updatePick(i, { id: v.toLowerCase() })} placeholder="general" dir="ltr" testid={`admin-quick-pick-id-${i}`} error={fieldErrors[`picks.${i}.id`]} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label required>Recommended tool</Label>
                <SelectInput value={p.tool_slug} onChange={(v) => updatePick(i, { tool_slug: v })} options={toolOptions} testid={`admin-quick-pick-tool-${i}`} error={fieldErrors[`picks.${i}.tool_slug`]} />
              </div>
            </div>
            <BilingualField
              name={`picks.${i}.need`}
              label="Need label (what this answers)"
              required
              valueAr={p.need.ar}
              valueEn={p.need.en}
              onChangeAr={(v) => updateNeed(i, "ar", v)}
              onChangeEn={(v) => updateNeed(i, "en", v)}
              errors={fieldErrors}
            />
            <FieldError error={fieldErrors[`picks.${i}.need.ar`]} id={`admin-quick-pick-need-error-${i}`} />
          </div>
        ))}

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={addPick}
            disabled={picks.length >= 4}
            className="sas-btn sas-btn-ghost"
            data-testid="admin-quick-pick-add"
          >
            <Plus size={15} aria-hidden /> Add pick
          </button>
          <button
            type="submit"
            disabled={saving}
            className="sas-btn sas-btn-primary"
            data-testid="admin-quick-picks-save"
          >
            <Save size={15} aria-hidden /> {saving ? "Saving…" : "Save quick picks"}
          </button>
        </div>
      </form>
    </div>
  );
}
